import type { CSSProperties } from "react";

/**
 * Laad-skelet (shimmer), BarberHost-stijl. Een zacht blok (`.sk`, styles.css) met een
 * bewegende glans dat de layout aanhoudt terwijl data laadt — getoond bij de eerste load
 * én bij een period-switch-refetch, zodat "Overzicht" niet van/naar een kale spinner springt.
 * Puur presentatie: `aria-hidden`, geen interactie.
 */
export function Skeleton({
  w,
  h,
  r,
  className = "",
  style,
}: {
  w?: number | string;
  h?: number | string;
  r?: number | string;
  className?: string;
  style?: CSSProperties;
}) {
  return <span className={`sk ${className}`.trim()} style={{ display: "block", width: w, height: h, borderRadius: r, ...style }} aria-hidden />;
}

/** KPI-rooster-skelet: `n` kaarten in hetzelfde `.kpi-grid` als de echte KPIs. */
export function SkeletonKpiGrid({ n = 4 }: { n?: number }) {
  return (
    <div className="kpi-grid" aria-hidden>
      {Array.from({ length: n }).map((_, i) => (
        <div className="card kpi" key={i}>
          <Skeleton className="sk-text" w="45%" />
          <Skeleton h={28} w="68%" style={{ margin: "10px 0" }} />
          <Skeleton className="sk-text" w="34%" />
        </div>
      ))}
    </div>
  );
}

/** Kaart met een titelregel + één groot blok (grafiek-plek). */
export function SkeletonChartCard({ h = 220, title = true }: { h?: number; title?: boolean }) {
  return (
    <div className="card" aria-hidden>
      {title && <Skeleton className="sk-text" w="26%" style={{ marginBottom: 14 }} />}
      <Skeleton h={h} r={12} />
    </div>
  );
}

/** Rijen (lijst/tabel): een rond icoon + een tekstregel van wisselende breedte. */
export function SkeletonRows({ rows = 6, icon = true }: { rows?: number; icon?: boolean }) {
  return (
    <div aria-hidden style={{ display: "grid", gap: 12 }}>
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} style={{ display: "flex", gap: 12, alignItems: "center" }}>
          {icon && <Skeleton w={30} h={30} r={8} />}
          <Skeleton className="sk-text" w={`${52 + (i % 3) * 12}%`} />
        </div>
      ))}
    </div>
  );
}

/** Kaart met een titel + rijen (apps-lijst / tabel / tijdlijn). */
export function SkeletonTableCard({ rows = 6, icon = true }: { rows?: number; icon?: boolean }) {
  return (
    <div className="card" aria-hidden>
      <Skeleton className="sk-text" w="22%" style={{ marginBottom: 16 }} />
      <SkeletonRows rows={rows} icon={icon} />
    </div>
  );
}

/** Overzicht (desktop): KPI-rooster + grafiek + apps-tabel. */
export function OverviewSkeleton() {
  return (
    <div aria-hidden>
      <SkeletonKpiGrid n={6} />
      <SkeletonChartCard h={240} />
      <div style={{ height: 16 }} />
      <div className="grid-2">
        <SkeletonTableCard rows={5} icon={false} />
        <SkeletonTableCard rows={5} icon={false} />
      </div>
      <SkeletonTableCard rows={3} icon={false} />
      <div style={{ height: 16 }} />
      <div className="grid-2">
        <SkeletonTableCard rows={4} icon={false} />
        <SkeletonTableCard rows={4} icon={false} />
      </div>
      <SkeletonTableCard rows={3} icon={false} />
      <div style={{ height: 16 }} />
      <SkeletonTableCard rows={7} />
    </div>
  );
}

/** Overzicht (mobiel): MRR-kaart + 2×2 KPIs + apps-lijst. */
export function OverviewMobileSkeleton() {
  return (
    <div aria-hidden>
      <section className="card">
        <Skeleton className="sk-text" w="30%" />
        <Skeleton h={34} w="58%" style={{ margin: "10px 0" }} />
        <Skeleton h={150} r={12} />
      </section>
      <div className="m-kpi-hero">
        <div className="card kpi">
          <Skeleton className="sk-text" w="40%" />
          <Skeleton h={40} w="62%" style={{ margin: "10px 0" }} />
          <Skeleton className="sk-text" w="28%" />
        </div>
      </div>
      <div className="m-kpi-grid">
        {Array.from({ length: 4 }).map((_, i) => (
          <div className="card kpi" key={i}>
            <Skeleton className="sk-text" w="55%" />
            <Skeleton h={22} w="70%" style={{ marginTop: 8 }} />
          </div>
        ))}
      </div>
      <SkeletonTableCard rows={4} icon={false} />
      <SkeletonTableCard rows={4} icon={false} />
      <SkeletonTableCard rows={3} icon={false} />
      <SkeletonTableCard rows={3} icon={false} />
      <SkeletonTableCard rows={5} />
    </div>
  );
}

/** Split-pagina (Klanten): lijst links + detail rechts. */
export function SplitSkeleton() {
  return (
    <div className="split" aria-hidden>
      <aside className="card split-list">
        <Skeleton h={40} r={10} style={{ marginBottom: 12 }} />
        <SkeletonRows rows={8} />
      </aside>
      <div className="split-detail">
        <SkeletonKpiGrid n={4} />
        <SkeletonChartCard h={180} />
      </div>
    </div>
  );
}

/** Generiek paneel (Gebruik / app-detail / Toestellen): KPIs + optioneel grafiek + rijen. */
export function PanelSkeleton({ kpis = 4, chart = true, rows = 0 }: { kpis?: number; chart?: boolean; rows?: number }) {
  return (
    <div aria-hidden>
      {kpis > 0 && <SkeletonKpiGrid n={kpis} />}
      {chart && <SkeletonChartCard h={220} />}
      {rows > 0 && (
        <>
          <div style={{ height: 16 }} />
          <SkeletonTableCard rows={rows} />
        </>
      )}
    </div>
  );
}
